import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { circuitFor } from "@/lib/circuits";
import { formatRange, pad } from "@/lib/race";
import { useLang } from "@/context/LangContext";

const isBlack = (c) => typeof c === "string" && c.startsWith("rgb(0, 0, 0");

const CleanSvg = ({ src }) => {
  const ref = useRef(null);
  useEffect(() => {
    let alive = true;
    fetch(src).then((r) => r.text()).then((txt) => {
      const box = ref.current;
      if (!alive || !box) return;
      box.innerHTML = txt;
      const svg = box.querySelector("svg");
      if (!svg) return;
      if (!svg.getAttribute("viewBox")) svg.setAttribute("viewBox", `0 0 ${parseFloat(svg.getAttribute("width")) || 1000} ${parseFloat(svg.getAttribute("height")) || 600}`);
      svg.removeAttribute("width");
      svg.removeAttribute("height");
      svg.querySelectorAll("text,circle,ellipse,image").forEach((n) => n.remove());
      svg.querySelectorAll("path,polygon,polyline,rect,line").forEach((n) => {
        const cs = getComputedStyle(n);
        if (!isBlack(cs.fill) && !isBlack(cs.stroke)) { n.remove(); return; }
        if (isBlack(cs.fill)) n.style.fill = "#FFFFFF";
        if (isBlack(cs.stroke)) n.style.stroke = "#FFFFFF";
      });
      const all = svg.getBBox();
      const minLen = Math.max(all.width, all.height) * 0.04;
      svg.querySelectorAll("path,polygon,polyline,rect,line").forEach((n) => {
        const b = n.getBBox();
        if (Math.max(b.width, b.height) < minLen) n.remove();
      });
      const fit = svg.getBBox();
      svg.setAttribute("viewBox", `${fit.x - 20} ${fit.y - 20} ${fit.width + 40} ${fit.height + 40}`);
    }).catch(() => {});
    return () => { alive = false; };
  }, [src]);
  return <div ref={ref} data-testid="circuit-map-svg" className="h-full w-full [&_svg]:h-full [&_svg]:w-full" />;
};

const TrackMap = ({ c }) => {
  if (c.image) {
    return <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="h-full w-full"><CleanSvg src={c.image} /></motion.div>;
  }
  return (
    <svg viewBox={`0 0 ${c.w} ${c.h}`} className="h-full w-full" aria-label={c.name} data-testid="circuit-map-svg">
      <path d={c.d} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="26" strokeLinejoin="round" strokeLinecap="round" />
      <motion.path d={c.d} fill="none" stroke="#FFFFFF" strokeWidth="9" strokeLinejoin="round" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.2, ease: [0.65, 0, 0.35, 1] }} />
      <circle r="13" fill="#3A5BC7" stroke="#FFFFFF" strokeWidth="4">
        <animateMotion dur="9s" repeatCount="indefinite" path={c.d} begin="2.2s" />
      </circle>
    </svg>
  );
};

const Info = ({ k, v, testId }) => (
  <div className="border-t border-white/10 py-4">
    <dt className="label-mono text-[11px] text-white/80">{k}</dt>
    <dd className="mt-1 font-display text-2xl font-extrabold uppercase text-white" data-testid={testId}>{v}</dd>
  </div>
);

export const CircuitDialog = ({ race, onClose }) => {
  const { t, lang } = useLang();
  const c = circuitFor(race);
  const L = t.circuit;

  return (
    <Dialog open={!!race} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-lenis-prevent data-testid="circuit-dialog" className="max-h-[92vh] max-w-5xl overflow-y-auto border-white/10 bg-ink p-0">
        <div className="grid grid-cols-1 md:grid-cols-5">
          <div className="relative min-h-[300px] bg-surface p-6 md:col-span-3 md:min-h-[520px] md:p-10">
            <p className="label-mono text-white">{race?.championship === "E4" ? "E4" : "Italian F4"} · R{pad(race?.round ?? 0)}</p>
            <div className="mt-6 h-[240px] md:h-[400px]">{c ? <TrackMap c={c} /> : <p className="text-zinc-400">{L.no_map}</p>}</div>
          </div>
          <div className="p-6 md:col-span-2 md:p-10">
            <DialogTitle className="font-display text-5xl font-black uppercase leading-[0.9] text-white" data-testid="circuit-dialog-title">{race?.circuit}</DialogTitle>
            <DialogDescription className="mt-3 text-sm text-zinc-300">{c?.name}</DialogDescription>
            <p className="label-mono mt-6 text-volt" data-testid="circuit-dialog-dates">{formatRange(race?.date_start, race?.date_end, t.months)}</p>
            {c && (
              <dl className="mt-6">
                <Info k={L.location} v={c.location[lang]} testId="circuit-info-location" />
                <div className="grid grid-cols-2 gap-x-6">
                  <Info k={L.length} v={`${c.length} km`} testId="circuit-info-length" />
                  <Info k={L.turns} v={c.turns} testId="circuit-info-turns" />
                  <Info k={L.opened} v={c.opened} testId="circuit-info-opened" />
                  <Info k={L.record} v={c.record.time} testId="circuit-info-record" />
                </div>
                <p className="mt-1 text-xs text-zinc-400">{L.record_by} {c.record.by} · {c.record.cls} · {c.record.year}</p>
              </dl>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
